import React from 'react';
import Header from './Header';
import Footer from './Footer';
import './CookiePolicy.css';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import BackToTopButton from './BackToTopButton';

import policyMeta from '../policyMeta.json';

const cookiePolicyDate = policyMeta.insertDate;
const cookiePolicyURL = policyMeta.insertWebsiteURL;
const cookiePolicyEmail = `[${policyMeta.supportEmail}](mailto:${policyMeta.supportEmail})`;

const cookiePolicyMarkdown = `
This Cookie Policy explains how Appstoria Inc. (“Company”, “we,” “us,” or “our”) uses cookies and similar technologies on our website, [${cookiePolicyURL}](${cookiePolicyURL}) (“Website”), and in connection with our mobile applications (“Apps”). It also explains your rights and choices regarding these technologies under applicable laws.

By continuing to use our Website or Apps, you consent to our use of cookies as described in this policy, unless you have disabled them through your browser settings or consent preferences.

### **1. What Are Cookies?**

Cookies are small data files placed on your device (computer, tablet, smartphone) when you visit a website. They are widely used to make websites work efficiently, provide reporting information, and remember user preferences.

Cookies placed directly by this Website are referred to as first-party cookies. Those placed by third parties (e.g., analytics or advertising partners) are known as third-party cookies and may be used to enable features such as behavioral advertising or embedded services.

In addition to cookies, we may use similar technologies like tracking pixels, web beacons, and local storage to gather information and support specific functionality. These technologies work in conjunction with cookies and may also rely on your consent.

### **2. Which Cookies We Use and Why**

We use both first-party and third-party cookies for a range of purposes, including the following:

* Strictly Necessary Cookies (“Essential”) – Required for the Website's operation. These include cookies that enable core functionality such as security, network management, and accessibility.

| Cookie Name | Expiration | Purpose |
| :---- | :---- | :---- |
| sid | Session | Used to analyze user interactions, preferences, and engagement metrics, as well as for debugging purposes. |
| is-tracking-enabled | Persist | Identifier indicating whether tracking is allowed |
| SAVED_PAYMENT_METHOD | Persist | Stores the user's saved payment method. |
| CURRENT_CHECKOUT_FLOW | Session | It tracks the user's current step in the checkout process to ensure a smooth and consistent purchase experience. |
| USER_DATA | Persist | Necessary user data, such as email, for form field display during payment. |
| cookie_banner | Persist | Manages the visibility state of the cookie banner. |

* Performance and Analytics Cookies – Used to collect aggregated data about Website traffic and usage to help us improve performance and functionality.

| Cookie Name | Expiry | Purpose |
| :---- | :---- | :---- |
| _ga | 2 years | To understand our website usage, and help us optimize our website by collecting and analyzing data about the behavior of visitors to our website (e.g. which website a person came from, which subpages were visited, or how often or for what duration a subpage was viewed). |
| ga_cid | 24 hours |  |
| segment-key | Persist | This cookie enables the website to analyze data related to user preferences, engagement metrics, and other relevant information to optimize targeting strategies. |
| TRACKING_PARAMS | Session |  |
| get_params_landings | Persist | To understand user behavior and enhance the overall user experience. |

* Advertising/Targeting Cookies – Set by third parties to deliver personalized advertisements based on your browsing behavior and interests (if applicable).

| Cookie Name | Source | Expiry | Purpose |
| :---- | :---- | :---- | :---- |
| _fbp | Facebook | 3 months | Deliver our advertising to those who visited our website when they are on Facebook or a digital platform powered by Facebook Advertising. |

### **3. Legal Basis for Use (for EEA Visitors)**

If you are located in the European Economic Area (EEA), our legal basis for processing personal data through cookies depends on the cookie type:

* **Essential cookies** – based on our legitimate interest in operating the Website.
* **Non-essential cookies** – used only after obtaining your consent.

### **4. Your Choices and Controls**

You have the right to control the use of non-essential cookies. Upon visiting the Website, you may be presented with a cookie consent banner that allows you to manage your preferences.

* Essential cookies cannot be disabled as they are required for the Website to function properly.
* Non-essential cookies (e.g., analytics or targeting) will only be activated after you give your consent, where required by law.

You can also manage cookie preferences through your browser settings. Please note that disabling some cookies may affect the functionality of the Website.

The duration for which a cookie remains on your device depends on whether it is a session cookie or a persistent cookie: (a) Session cookies expire when you close your browser; (b) Persistent cookies remain until they expire or are deleted via browser settings.

### **5. Browser-Based Cookie Management**

Most browsers allow you to control cookies through their settings. You can typically configure your browser to refuse cookies, delete existing ones, or notify you when cookies are being set. Refer to the help documentation of your browser for more guidance:

* [Chrome](https://support.google.com/chrome/answer/95647#zippy=%2Callow-or-block-cookies)
* [Internet Explorer](https://support.microsoft.com/en-us/windows/delete-and-manage-cookies-168dab11-0753-043d-7c16-ede5947fc64d)
* [Firefox](https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop?redirectslug=enable-and-disable-cookies-website-preferences&redirectlocale=en-US)
* [Safari](https://support.apple.com/en-ie/guide/safari/sfri11471/mac)
* [Edge](https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd)
* [Opera](https://help.opera.com/en/latest/web-preferences/)

You may also opt out of interest-based advertising by visiting:

* [Digital Advertising Alliance](http://www.aboutads.info/choices/)
* [Digital Advertising Alliance of Canada](https://youradchoices.ca/)
* [European Interactive Digital Advertising Alliance](http://www.youronlinechoices.com/)

### **6. Policy Updates**

We may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or our use of cookies. Any updates will be posted on this page, accompanied by a revised "Last Updated" date. We encourage you to review this policy periodically.

### **7. Contact**

If you have any questions about our use of cookies or this Cookie Policy, please contact us at ${cookiePolicyEmail}.
`;

const CookiePolicy = () => (
  <div className="page-outer cookie-policy-page">
    <Header />
    <main className="cookie-policy-main main-content">
      <div className="cookie-policy-header-bg">
        <div className="cookie-policy-header">
          <h1>Cookie Policy</h1>
          <div className="cookie-policy-date">Effective Date: {cookiePolicyDate}</div>
        </div>
      </div>
      <div className="cookie-policy-container">
        <div className="cookie-policy-inner">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{cookiePolicyMarkdown}</ReactMarkdown>
        </div>
      </div>
    </main>
    <Footer />
    <BackToTopButton />
  </div>
);

export default CookiePolicy;
